import get from 'lodash/get';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useCustomSides from './hooks/useCustomSidePanelCustomSideTab';

import { SELECTION_CIRCLES, UNIT_MEASURE } from '~/core/constants';
import { CircleAction, CustomSideType, DesignSection } from '~/core/enums';
import { CustomSideModel } from '~/core/models/predefined-custom-side.model';
import { formatPriceUnitOrFree } from '~/core/utils';
import { useAppDispatch } from '~/redux/hooks';
import { updateSelectedCustomSide } from '~/redux/slices/customSide';
import { updateSelectionCircles } from '~/redux/slices/fabric';
import { useDrawingShapeContext } from '~contexts';
import SidePanelSectionTab from '~organisms/SidePanelSectionTab';

const SidePanelCustomSideTab = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { areCustomSidesLoading: isFetching, customSides = [] } =
    useCustomSides();
  const {
    getters: { actualDrawingShapes },
  } = useDrawingShapeContext();

  const options = customSides.filter(
    (side) => side.type !== CustomSideType.REMOVE_CUSTOM_SIDE
  );
  const [countersLimit, optionsCounters] = useMemo(
    () =>
      actualDrawingShapes.reduce<[number, Record<string, number>]>(
        ([limit, counters], { drawingEdges }) =>
          drawingEdges.reduce(
            ([limit, counters], { customSideType: sideType }) => [
              limit,
              sideType
                ? { ...counters, [sideType]: get(counters, sideType, 0) + 1 }
                : counters,
            ],
            [limit + drawingEdges.length, counters]
          ),
        [0, {}]
      ),
    [actualDrawingShapes]
  );

  const handleUpdate =
    (action: CircleAction) => (customSide: CustomSideModel) => {
      const selectionCircles = {
        selection: SELECTION_CIRCLES.FOR_CUSTOM_SIDES,
        action,
      };

      dispatch(updateSelectedCustomSide(customSide));
      dispatch(updateSelectionCircles(selectionCircles));
    };

  return (
    <SidePanelSectionTab<CustomSideModel>
      hidePrice
      section={DesignSection.CUSTOM_SIDES}
      removeModalTitle={({ selectedOptionId }) =>
        t('DESIGN_TOOLBAR.REMOVE_MODAL.TITLE', {
          option:
            selectedOptionId === CustomSideType.BUMP_OUT
              ? 'bump out'
              : 'radius out',
        })
      }
      removeModalDescription={({ selectedOptionId }) =>
        t('DESIGN_TOOLBAR.REMOVE_MODAL.DESCRIPTION', {
          option:
            selectedOptionId === CustomSideType.BUMP_OUT
              ? 'bump out'
              : 'radius out',
        })
      }
      hookArgs={{
        options,
        isFetching,
        stateAddOption: handleUpdate(CircleAction.ADD),
        stateRemoveOption: handleUpdate(CircleAction.REMOVE),
        optionsCounters,
        getId: (option) => option.type,
        countersLimit,
        mapOptionToDisplay: (option) => ({
          title: option.title,
          image: option.image,
          price: formatPriceUnitOrFree(0, UNIT_MEASURE.CUSTOM_WORK, true),
        }),
      }}
    />
  );
};

export default SidePanelCustomSideTab;
